// deps
import React, { memo, useState, useCallback, useEffect, useContext, useMemo } from "react";

//internal deps
import {
    DeliveryInstructionDetail,
    DeliveryInstructionCroutonDetail,
    CroutonPriority,
    CroutonIds,
} from "@swiggy-private/cart-types";
import { DELIVERY_INSTRUCTION_WIDGET_TEST_IDS } from "@swiggy-private/cart-test-ids";
import { ScreenReaderOnly, ElementObserver } from "@swiggy-private/core-ui";
import { CartOverlaysContext, isPopFlow } from "@swiggy-private/checkout-core";

// constants
import { TEXTS } from "@cart-ui/constants";

// components
import DeliveryInstructionItemCollection from "./components/DeliveryInstructionItemCollection";
import VoiceDirectionTooltip from "./components/VoiceDirectionTooltip";

import styles from "./styles/DeliveryInstruction.scss";

// helpers
import { hasSeenDICrouton, updateSessionStorageAfterCroutonClick } from "@cart-ui/helpers/DeliveryInstruction";
import classNames from "classnames";

export const DELIVERY_INSTRUCTIONS_CONTAINER_ID = "delivery-instructions-container";

export interface DeliveryInstructionProps {
    instructionArray: DeliveryInstructionDetail[];
    displayText?: string;
    showBenefitText?: boolean;
    showVoiceDirectionTooltip: boolean;
    deliveryInstructionCroutonDetail?: DeliveryInstructionCroutonDetail;
    canShowCrouton: boolean;
    scrollToDIContainer: VoidFunction;
    onItemClick: (itemType: DeliveryInstructionDetail) => void;
    analyticsEvents: {
        onVoiceDirectionTooltipImpression: (displayText: string) => void;
        onDeliveryInstructionCroutonClick: VoidFunction;
        onDeliveryInstructionCroutonImpression: VoidFunction;
    };
    setCroutonVisibility: (croutonVisibility: boolean) => void;
}

const DeliveryInstruction: React.FC<DeliveryInstructionProps> = ({
    instructionArray,
    onItemClick,
    showBenefitText,
    deliveryInstructionCroutonDetail,
    canShowCrouton,
    scrollToDIContainer,
    analyticsEvents,
    showVoiceDirectionTooltip,
    setCroutonVisibility,
}: DeliveryInstructionProps) => {
    const { text: croutonText, imgId: croutonImg } = deliveryInstructionCroutonDetail || {};
    const [showCrouton, setShowCrouton] = useState<boolean>(canShowCrouton && !hasSeenDICrouton());

    const { registerCrouton } = useContext(CartOverlaysContext);
    const isPopCart = isPopFlow();

    const impressionDICrouton = useCallback(() => {
        analyticsEvents.onDeliveryInstructionCroutonImpression();
    }, [analyticsEvents]);

    const onViewEnter = useCallback(() => {
        setShowCrouton(false);
    }, []);

    const onCroutonClick = useCallback(() => {
        scrollToDIContainer();
        updateSessionStorageAfterCroutonClick();
        analyticsEvents.onDeliveryInstructionCroutonClick();
        setShowCrouton(false);
    }, [analyticsEvents, scrollToDIContainer]);

    useEffect(() => {
        if (!canShowCrouton && showCrouton) {
            setShowCrouton(false);
        }
    }, [canShowCrouton, showCrouton]);

    const containerClass = classNames(styles.container, styles.containerCartV2);

    const croutonContent = useMemo(
        () => ({
            title: croutonText,
            postTextIcon: croutonImg,
            onClick: onCroutonClick,
            impressionEvent: impressionDICrouton,
            buttonClassName: styles.croutonButton,
            setCroutonVisibility: setCroutonVisibility,
        }),
        [croutonImg, croutonText, impressionDICrouton, onCroutonClick, setCroutonVisibility],
    );

    useEffect(() => {
        if (showCrouton && croutonText && !isPopCart) {
            registerCrouton({
                id: CroutonIds.DELIVERY_INSTRUCTION,
                priority: CroutonPriority.PRIORITY_ORDER_ONE,
                content: croutonContent,
            });
        }
    }, [croutonContent, croutonText, isPopCart, registerCrouton, showCrouton]);

    return (
        <div
            className={containerClass}
            data-testid={DELIVERY_INSTRUCTION_WIDGET_TEST_IDS.DELIVERY_INSTRUCTION_WIDGET_ID}
            id={DELIVERY_INSTRUCTIONS_CONTAINER_ID}
        >
            <ElementObserver onViewEnter={onViewEnter} className={styles.containerSensor}>
                <div />
            </ElementObserver>
            <ScreenReaderOnly
                text={`${TEXTS.DELIVERY_INSTRUCTIONS}  ${
                    showBenefitText ? TEXTS.DELIVERY_INSTRUCTION_BENEFIT_TEXT : ""
                }`}
            />
            <DeliveryInstructionItemCollection instructionArray={instructionArray} onItemClick={onItemClick} />
            {showVoiceDirectionTooltip && (
                <VoiceDirectionTooltip
                    onVoiceDirectionTooltipImpression={analyticsEvents.onVoiceDirectionTooltipImpression}
                />
            )}
        </div>
    );
};

export default memo(DeliveryInstruction);
